<template>
  <span
    v-if="visible"
    class="lucy-spark-row"
  >
    <slot name="before" />
    <span
      class="lucy-spark-wrap"
      @mouseenter.stop
      @mouseover.stop
      @mousemove.stop
      @mouseleave.stop
    >
      <v-menu
        v-model="open"
        open-on-hover
        :open-delay="80"
        :close-delay="320"
        offset-y
        :nudge-bottom="2"
        :close-on-content-click="false"
        max-width="300"
        min-width="260"
        z-index="40"
        content-class="lucy-spark-menu"
      >
        <template #activator="{ on, attrs }">
          <button
            type="button"
            class="lucy-spark"
            :aria-label="$t('lucy.askAbout')"
            v-bind="attrs"
            v-on="activatorOn(on)"
          >L</button>
        </template>
        <LucyCard
          :metric="metric"
          @go="go"
          @ask="ask"
        />
      </v-menu>
    </span>
  </span>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import {SeoScoreMetricName, SeoScoreStatus} from '@/model/SeoScore';
    import SeoScoreMetricsMixin from '@/mixins/SeoScoreMetricsMixin';
    import {firstAiVisibilityGap, lucyMetricIsDone, openLucyPanel} from '@/service/lucyCoach';
    import {focusSeoMetric} from '@/service/seoScoreFocus';
    import LucyCard from '@/components/lucy/LucyCard.vue';
    import {Article} from '@/model/Article';

    type LucySparkVm = Vue & {
        getStatus(name: SeoScoreMetricName): SeoScoreStatus;
    };

    export default Vue.extend({
        name: 'LucySpark',
        components: {LucyCard},
        mixins: [SeoScoreMetricsMixin],
        props: {
            metric: {
                type: String as PropType<SeoScoreMetricName>,
                required: true
            },
            hideWhenDone: {
                type: Boolean,
                default: true
            }
        },
        data: () => ({
            open: false
        }),
        computed: {
            visible(): boolean {
                if (this.$store.getters.sharePreview) {
                    return false;
                }
                if (!this.hideWhenDone) {
                    return true;
                }
                const article = this.$store.getters.article as Article | undefined;
                if (!article || !article.id) {
                    return false;
                }
                const vm = this as unknown as LucySparkVm;
                return !lucyMetricIsDone(this.metric, vm.getStatus(this.metric), article);
            }
        },
        methods: {
            activatorOn(on: Record<string, (event: Event) => void>): Record<string, (event: Event) => void> {
                const stop = (handler?: (event: Event) => void) => (event: Event): void => {
                    event.stopPropagation();
                    handler?.(event);
                };
                return {
                    ...on,
                    click: stop(on.click),
                    mouseenter: stop(on.mouseenter),
                    mouseover: stop(on.mouseover),
                    mousemove: stop(on.mousemove),
                    mouseleave: stop(on.mouseleave),
                    focus: stop(on.focus)
                };
            },
            go(): void {
                this.open = false;
                const target = this.metric === 'aiVisibility'
                    ? (firstAiVisibilityGap(this.$store.getters.article) || this.metric)
                    : this.metric;
                if (this.$route.name !== 'Article' && this.$store.getters.article.id) {
                    this.$router.push({
                        path: '/article/' + this.$store.getters.article.id,
                        query: {seoFocus: target}
                    }).catch(() => undefined);
                    return;
                }
                focusSeoMetric(target);
            },
            ask(): void {
                this.open = false;
                openLucyPanel({metric: this.metric});
            }
        }
    });
</script>

<style scoped lang="sass">
.lucy-spark-row
    display: inline-flex
    align-items: center
    flex: 0 0 auto

.lucy-spark-wrap
    display: inline-flex
    align-items: center
    justify-content: center
    flex: 0 0 22px
    width: 22px
    height: 22px
    position: relative
    z-index: 2

.lucy-spark-wrap ::v-deep .v-menu
    display: inline-flex !important

.lucy-spark
    width: 22px
    height: 22px
    border: 0
    border-radius: 50%
    background: linear-gradient(135deg, #cd7ec2, #8e3d82)
    color: #fff
    font-size: 11px
    font-weight: 800
    line-height: 1
    cursor: pointer
    flex: 0 0 22px
    box-shadow: 0 0 0 3px rgba(205, 126, 194, 0.22)
    display: inline-flex
    align-items: center
    justify-content: center
    padding: 0

    &:hover
        box-shadow: 0 0 0 4px rgba(205, 126, 194, 0.35)
</style>
